import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    ArrowLeft, MapPin, MessageCircle, Phone, Share2, Users,
    Store, ShoppingCart, Eye, Loader2, Check, UserPlus,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Shop() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [following, setFollowing] = useState(false);
    const [followers, setFollowers] = useState(0);
    const [followBusy, setFollowBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/shops/${slug}`);
                setData(data);
                setFollowing(data.is_following);
                setFollowers(data.followers_count);
            } catch (e) {
                if (e?.response?.status === 404) {
                    toast.error("Boutique introuvable");
                    navigate("/feed", { replace: true });
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [slug, navigate]);

    const toggleFollow = async () => {
        if (!user) return navigate("/login");
        if (followBusy) return;
        setFollowBusy(true);
        const wasFollowing = following;
        setFollowing(!wasFollowing);
        setFollowers((n) => n + (wasFollowing ? -1 : 1));
        try {
            const r = await api.post(`/shops/${slug}/follow`);
            setFollowing(r.data.following);
            setFollowers(r.data.followers_count);
            toast.success(r.data.following ? "Boutique suivie !" : "Ne suit plus");
        } catch (e) {
            setFollowing(wasFollowing);
            setFollowers((n) => n + (wasFollowing ? 1 : -1));
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setFollowBusy(false);
        }
    };

    const shareShop = async () => {
        const url = `${window.location.origin}/shop/${slug}`;
        const shopName = data?.shop?.shop_name || "Boutique";
        try {
            if (navigator.share) {
                await navigator.share({ title: shopName, text: `Découvrez ${shopName} sur VIDMAR`, url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Lien copié !");
            }
        } catch (err) {
            console.debug("Share cancelled", err?.name);
        }
    };

    const orderProduct = async (productId) => {
        try {
            const { data: r } = await api.post(`/products/${productId}/order`);
            window.open(r.whatsapp_url, "_blank");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-[#050505]">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF5722]" />
            </div>
        );
    }

    if (!data) return null;

    const { shop, products, is_owner } = data;

    return (
        <div className="min-h-screen bg-[#050505] text-white pb-32 grain-bg">
            {/* Cover header */}
            <div className="relative h-56 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF5722]/40 via-black to-[#25D366]/20" />
                {products?.[0] && (
                    <img
                        src={fileUrl(products[0].image_path)}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-30 blur-md scale-110"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />

                <div className="relative z-10 max-w-lg mx-auto px-4 pt-6">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate(-1)}
                            data-testid="shop-back-btn"
                            className="w-10 h-10 glass rounded-full flex items-center justify-center"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={shareShop}
                            data-testid="shop-share-btn"
                            className="w-10 h-10 glass rounded-full flex items-center justify-center"
                        >
                            <Share2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Shop info */}
            <div className="max-w-lg mx-auto px-4 -mt-20 relative z-20">
                <div className="flex items-end gap-4 mb-4">
                    <div className="w-24 h-24 rounded-2xl bg-white/10 border-2 border-[#050505] overflow-hidden flex items-center justify-center shadow-2xl">
                        {shop.picture ? (
                            <img src={shop.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <Store className="w-10 h-10 text-white/70" />
                        )}
                    </div>
                </div>

                <h1 className="font-display font-black text-3xl tracking-tight" data-testid="shop-name">
                    {shop.shop_name}
                </h1>
                <p className="text-white/50 font-body text-sm mt-1" data-testid="shop-slug">@{shop.shop_slug}</p>

                <div className="flex items-center gap-4 mt-3 text-sm text-white/70 font-body">
                    {shop.city && (
                        <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {shop.city}
                        </span>
                    )}
                    <span className="flex items-center gap-1" data-testid="shop-followers">
                        <Users className="w-4 h-4" /> <b className="text-white">{followers}</b>
                        <span className="text-white/50">abonnés</span>
                    </span>
                    <span className="flex items-center gap-1">
                        <Store className="w-4 h-4" /> <b className="text-white">{products.length}</b>
                        <span className="text-white/50">produits</span>
                    </span>
                </div>

                {/* Actions */}
                <div className="mt-5 grid grid-cols-3 gap-2">
                    {is_owner ? (
                        <Link
                            to="/dashboard"
                            className="col-span-3 orange-btn h-12 rounded-full flex items-center justify-center gap-2 font-body font-bold"
                            data-testid="shop-manage-btn"
                        >
                            Gérer ma boutique
                        </Link>
                    ) : (
                        <>
                            <button
                                onClick={toggleFollow}
                                disabled={followBusy}
                                data-testid="shop-follow-btn"
                                className={`col-span-1 h-12 rounded-full flex items-center justify-center gap-1.5 font-body font-bold text-sm transition-colors ${
                                    following ? "bg-white/10 text-white border border-white/20" : "orange-btn"
                                }`}
                            >
                                {following ? (
                                    <><Check className="w-4 h-4" /> Suivi</>
                                ) : (
                                    <><UserPlus className="w-4 h-4" /> Suivre</>
                                )}
                            </button>
                            {shop.whatsapp_number && (
                                <a
                                    href={`https://wa.me/${shop.whatsapp_number.replace(/\D/g, "")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="shop-whatsapp-btn"
                                    className="col-span-1 wa-btn h-12 rounded-full flex items-center justify-center gap-1.5 text-white font-body font-bold text-sm"
                                >
                                    <MessageCircle className="w-4 h-4" /> WhatsApp
                                </a>
                            )}
                            {shop.call_number && (
                                <a
                                    href={`tel:${shop.call_number}`}
                                    data-testid="shop-call-btn"
                                    className="col-span-1 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center gap-1.5 font-body font-bold text-sm"
                                >
                                    <Phone className="w-4 h-4" /> Appeler
                                </a>
                            )}
                        </>
                    )}
                </div>

                {/* Products grid */}
                <div className="mt-8">
                    <h2 className="font-display font-black text-2xl mb-3">Produits</h2>
                    {products.length === 0 ? (
                        <div className="glass rounded-2xl p-6 text-center">
                            <p className="text-white/60 font-body">Aucun produit publié pour le moment</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            {products.map((p) => (
                                <div
                                    key={p.id}
                                    data-testid={`shop-product-${p.id}`}
                                    className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-white/5 group"
                                >
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/watch/${p.id}`)}
                                        data-testid={`shop-open-product-${p.id}`}
                                        className="absolute inset-0 w-full h-full text-left"
                                        aria-label={`Voir ${p.title}`}
                                    >
                                        <img src={fileUrl(p.image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent p-3 flex flex-col justify-end">
                                            <p className="font-display font-bold text-sm text-white line-clamp-1">{p.title}</p>
                                            <p className="font-display font-black text-lg text-[#FFC107]">
                                                {Number(p.price).toLocaleString("fr-FR")} F
                                            </p>
                                            <div className="flex items-center justify-between mt-1">
                                                <div className="flex items-center gap-2 text-[10px] text-white/70 font-body">
                                                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{p.views || 0}</span>
                                                    <span className="flex items-center gap-1"><ShoppingCart className="w-3 h-3" />{p.orders || 0}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                    {!is_owner && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); orderProduct(p.id); }}
                                            data-testid={`shop-order-${p.id}`}
                                            className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center active:scale-95 z-10"
                                        >
                                            <MessageCircle className="w-4 h-4 text-white" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <BottomNav />
        </div>
    );
}
